import React, { useState } from 'react';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { Button } from '../../../mui-builder';
import Box from '@mui/material/Box';

interface EditableFieldLayoutProps {
  editFieldInput: React.ReactElement;
  fieldComponent: React.ReactElement;
  isUserCanChange: boolean;
  label: string;
  isRequired?: boolean;
  isEditing?: boolean;
  onCancel: () => void;
  onChangeCallback?: (e: object) => void;
  helpText?: string;
  needToClearAfterModeSwitch?: boolean;
}

const EditableFieldLayout = ({
  editFieldInput,
  fieldComponent,
  isUserCanChange,
  onChangeCallback = () => {},
  label,
  isRequired = false,
  onCancel,
  helpText,
  needToClearAfterModeSwitch = true,
  isEditing: initialIsEditing = false,
}: EditableFieldLayoutProps) => {
  const [isEditing, setEditing] = useState(initialIsEditing);

  const input = (
    <>
      <Button
        type="button"
        variant="text"
        onClick={() => {
          setEditing(false);
          if (onCancel) onCancel();
        }}
        size="small"
        className="editable-field-cancel"
        sx={{
          position: 'absolute',
          top: '-12px',
          right: 0,
          zIndex: 1,
          fontSize: '12px',
          lineHeight: '14px',
          backgroundColor: 'common.white',
        }}
      >
        cancel
      </Button>
      {editFieldInput}
    </>
  );

  const field = (
    <>
      <FormLabel required={isRequired && !!label}>{label}</FormLabel>
      <Box className="editable-field__field-wrapper" sx={{ mt: '11px' }}>
        {fieldComponent}
        {isUserCanChange && (
          <Button
            type="button"
            variant="text"
            size="small"
            className="editable-field__change-button"
            sx={{ ml: '2px', fontSize: '12px', lineHeight: '14px', transform: 'translateY(-1px)' }}
            onClick={() => {
              setEditing(true);

              if (needToClearAfterModeSwitch && onChangeCallback) {
                onChangeCallback({
                  target: {
                    value: null,
                  },
                });
              }
            }}
          >
            change
          </Button>
        )}
      </Box>
    </>
  );

  const displayedComponent = isEditing ? input : field;

  return (
    <Box
      className="editable-field__wrapper"
      sx={{
        position: 'relative',
        width: '100%',
        mb: '20px',
        '& .form-group': { mt: 0, mb: 0 },
        '& > .MuiFormLabel-root': { display: 'block' },
      }}
    >
      {displayedComponent}
      {helpText && <FormHelperText>{helpText}</FormHelperText>}
    </Box>
  );
};

export default EditableFieldLayout;
